import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { LeaderboardTab } from "./leaderboard_tab.js";
import { playerCountry } from "../../../api/leaderboard/leaderboard_country.js";

const TAB_W_DESIGN        = 314;
const TAB_W_WIDE_DESIGN   = 434;
const TAB_H_DESIGN        = 120;
const TAB_ACTIVE_W_DESIGN = 320;
const TAB_ACTIVE_W_WIDE_DESIGN = 440;
const TAB_ACTIVE_H_DESIGN = 120;
const TAB_BORDER_PX_DESIGN  = 4;
const TAB_RADIUS_DESIGN = 20;
const TAB_TOP_W_DESIGN = 191;
const TAB_TOP_H_DESIGN = 44;
const TAB_TOP_Y_OFFSET_DESIGN = 22;
const TAB_TOP_TEXT_SIZE_DESIGN = 26;
const TEXT_SIZE_DESIGN      = 45;
const ACTIVE_TEXT_SIZE_DESIGN = 45;

export class LeaderboardScreenTabs extends UIElement
{
  private active_tab: LeaderboardTab = LeaderboardTab.Global;
  private readonly on_tab_change: ( tab: LeaderboardTab ) => void;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    on_tab_change: ( tab: LeaderboardTab ) => void
  )
  {
    super( ctx, x, y, width, height, false, false );
    this.on_tab_change = on_tab_change;
    this.createElements();
  }

  private tabX( index: number ): number
  {
    const tab_w = this.tabWidth();
    const gap = this.tabGap();
    return gap + index * ( tab_w + gap );
  }

  private tabGap(): number
  {
    return LayoutScaler.widthFromDesign( 9 );
  }

  private tabWidth( _is_active: boolean = false ): number
  {
    const gap = this.tabGap();
    return ( this.rect.getW() - gap * 4 ) / 3;
  }

  private createTabBg( index: number, x: number, y: number, is_active: boolean ): void
  {
    const w = this.tabWidth();
    const h = LayoutScaler.heightFromDesign( TAB_H_DESIGN );
    const is_middle = index === 1;
    const image_id = is_middle
      ? ( is_active ? ImgID.UI_LeaderboardSelectedMiddleTab : ImgID.UI_LeaderboardUnselectedMiddleTab )
      : ( is_active ? ImgID.UI_LeaderboardSelectedSideTab : ImgID.UI_LeaderboardUnselectedSideTab );
    const bg = new UINineSliceElement(
      this.ctx,
      x,
      y,
      w,
      h,
      image_id,
      "leaderboard_tab"
    );

    if ( index === 2 )
      bg.setFlipX( true );

    this.addChild( bg );
  }

  private createTabText( x: number, y: number, localization_key: string, is_active: boolean ): void
  {
    const design_h           = is_active ? TAB_ACTIVE_H_DESIGN : TAB_H_DESIGN;
    const design_text_size   = is_active ? ACTIVE_TEXT_SIZE_DESIGN : TEXT_SIZE_DESIGN;

    const w           = this.tabWidth( is_active );
    const h           = LayoutScaler.heightFromDesign( design_h );
    const text_size   = LayoutScaler.textSizeFromDesign( design_text_size );

    const text = new UITextElement( this.ctx, x, y, w, h, {
      localization_key,
      text_color: ColorScheme.white,
      text_align: MyTextAlign.Center,
      text_style: TextStyleScheme.geologica,
      font_size: text_size,
      font_weight: 900,
      vertical_align: MyTextVerticalAlign.Center,
      using_y_offset: false,
      ...TextStyleScheme.buttonShadow(),
      outline_color: is_active ? ColorScheme.leaderboard_tab_active_outline : ColorScheme.leaderboard_tab_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );
    // Selected tab: solid white. Unselected: white at 67% opacity.
    if ( !is_active )
      text.setOpacity( 0.67 );
    this.addChild( text );
  }

  private createSoonBadge( tab_x: number, tab_y: number ): void
  {
    const design_tab_w    = TAB_W_DESIGN;
    const design_top_w    = TAB_TOP_W_DESIGN;
    const design_top_h    = TAB_TOP_H_DESIGN;
    const design_y_offset = TAB_TOP_Y_OFFSET_DESIGN;
    const design_text_size = TAB_TOP_TEXT_SIZE_DESIGN;

    const tab_w   = this.tabWidth();
    const top_w   = LayoutScaler.widthFromDesign( design_top_w );
    const top_h   = LayoutScaler.heightFromDesign( design_top_h );
    const y_offset = LayoutScaler.yFromDesign( design_y_offset );
    const radius  = top_h / 2;
    const text_size = LayoutScaler.textSizeFromDesign( design_text_size );

    const top_x = tab_x + ( tab_w - top_w ) / 2;
    const top_y = tab_y - y_offset;

    const substrate = new RoundedRectangle(
      this.ctx,
      top_x,
      top_y,
      top_w,
      top_h,
      radius,
      "#021A64"
    );

    const label = new UITextElement(
      this.ctx,
      top_x,
      top_y,
      top_w,
      top_h,
      {
        localization_key: 'SOON',
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.geologica,
        font_size: text_size,
        font_weight: 900,
        vertical_align: MyTextVerticalAlign.Center,
      } );
    this.addChild( substrate );
    this.addChild( label );
  }

  private createElements(): void
  {
    const design_passive_tab_y = 20;
    const design_active_tab_y  = 20;

    const passive_tab_y = LayoutScaler.yFromDesign( design_passive_tab_y );  // 286 - 246
    const active_tab_y  = LayoutScaler.yFromDesign( design_active_tab_y );   // 281 - 246

    const x1 = this.tabX( 1 );
    const x2 = this.tabX( 2 );

    const x0 = this.tabX( 0 );

    const createTab = ( index: number, x: number, tab: LeaderboardTab, localization_key: string ): void =>
    {
      const is_active = this.active_tab === tab;
      const y = is_active ? active_tab_y : passive_tab_y;
      this.createTabBg( index, x, y, is_active );
      this.createTabText( x, y, localization_key, is_active );
      if ( is_active )
        return;

      const holder = new UIElementHolder( this.ctx, x, passive_tab_y, this.tabWidth(), LayoutScaler.heightFromDesign( TAB_H_DESIGN ), false, true );
      holder.setOnClick( () =>
      {
        this.active_tab = tab;
        this.removeAllChildren();
        this.createElements();
        this.on_tab_change( tab );
      } );
      this.addChild( holder );
    };

    createTab( 0, x0, LeaderboardTab.Global, 'GLOBAL' );

    // --- Country: the player's country board; "soon" while the server has not told it ---
    if ( playerCountry() )
      createTab( 1, x1, LeaderboardTab.Country, 'COUNTRY' );
    else
    {
      this.createTabBg( 1, x1, passive_tab_y, false );
      this.createTabText( x1, passive_tab_y, 'COUNTRY', false );
      this.createSoonBadge( x1, passive_tab_y );
    }

    // --- Friends ---
    this.createTabBg( 2, x2, passive_tab_y, false );
    this.createTabText( x2, passive_tab_y, 'FRIENDS', false );
    this.createSoonBadge( x2, passive_tab_y );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }

  public name(): string { return "LeaderboardScreenTabs"; }
}
